import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { escrowApi } from '@/services/api'
import { formatNumber } from '@/utils/format'
import { Search, AlertTriangle, Check, X, Plus, Trash2, ShieldCheck } from 'lucide-react'
import { useToast } from '@/hooks/useToast'

interface Transfer {
  from_user_id: number
  to_user_id: number
  amount: number
}

export default function EscrowManagement() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<any>(null)
  const [showModal, setShowModal] = useState(false)
  const [modalAction, setModalAction] = useState<'transfer' | 'return'>('transfer')
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [reason, setReason] = useState('')
  const [closeGroup, setCloseGroup] = useState(true)

  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch escrow groups
  const { data, isLoading } = useQuery({
    queryKey: ['escrow-groups', search, statusFilter],
    queryFn: () => escrowApi.list({ search, status: statusFilter }),
  })

  const groups = (data as any)?.data?.groups || []

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: (data: any) => escrowApi.transfer(data),
    onSuccess: () => {
      toast.success('Escrow transferred successfully')
      queryClient.invalidateQueries({ queryKey: ['escrow-groups'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to transfer escrow')
    },
  })

  // Return all mutation
  const returnAllMutation = useMutation({
    mutationFn: (data: any) => escrowApi.returnAll(data),
    onSuccess: () => {
      toast.success('All escrow returned successfully')
      queryClient.invalidateQueries({ queryKey: ['escrow-groups'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to return escrow')
    },
  })

  const openModal = (group: any, action: 'transfer' | 'return') => {
    setSelectedGroup(group)
    setModalAction(action)
    setReason('')
    setCloseGroup(true)

    // Initialize with empty transfers array for transfer action
    if (action === 'transfer') {
      setTransfers([])
    }

    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedGroup(null)
    setTransfers([])
    setReason('')
  }

  const addTransfer = () => {
    if (!selectedGroup?.members || selectedGroup.members.length < 2) return

    const firstMember = selectedGroup.members[0]
    const secondMember = selectedGroup.members[1]

    setTransfers([
      ...transfers,
      {
        from_user_id: firstMember.user_id,
        to_user_id: secondMember.user_id,
        amount: 0,
      },
    ])
  }

  const updateTransfer = (index: number, field: keyof Transfer, value: any) => {
    const updated = [...transfers]
    updated[index] = { ...updated[index], [field]: value }
    setTransfers(updated)
  }

  const removeTransfer = (index: number) => {
    setTransfers(transfers.filter((_, i) => i !== index))
  }

  const getTotalDeduction = (userId: number) => {
    return transfers
      .filter((t) => t.from_user_id === userId)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  }

  const getTotalAddition = (userId: number) => {
    return transfers
      .filter((t) => t.to_user_id === userId)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  }

  const validateTransfers = () => {
    if (transfers.length === 0) {
      return 'Please add at least one transfer'
    }

    for (const transfer of transfers) {
      if (transfer.amount <= 0) {
        return 'All transfer amounts must be greater than 0'
      }
      if (transfer.from_user_id === transfer.to_user_id) {
        return 'Cannot transfer to the same user'
      }
    }

    // Check if any member is overdrafting their escrow
    for (const member of selectedGroup?.members || []) {
      const totalDeduction = getTotalDeduction(member.user_id)
      if (totalDeduction > member.escrow_amount) {
        return `${member.first_name} ${member.last_name} has insufficient escrow (${formatNumber(member.escrow_amount)} coins)`
      }
    }

    return null
  }

  const handleSubmit = () => {
    if (modalAction === 'transfer') {
      const error = validateTransfers()
      if (error) {
        toast.error(error)
        return
      }

      transferMutation.mutate({
        group_id: selectedGroup.group_id,
        transfers,
        reason: reason || 'Admin escrow transfer',
        close_group: closeGroup,
      })
    } else if (modalAction === 'return') {
      returnAllMutation.mutate({
        group_id: selectedGroup.group_id,
        reason: reason || 'Mutual cancellation',
        close_group: closeGroup,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Escrow Management</h1>
          <p className="text-gray-600 mt-1">Manage escrow for barter groups and resolve conflicts</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by member name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select w-full"
          >
            <option value="">All Group Statuses</option>
            <option value="active">Active</option>
            <option value="conflict">Conflict</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Escrow Groups Found</h3>
          <p className="text-gray-500">There are no barter groups with held escrow at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group: any) => (
            <div key={group.group_id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Group #{group.group_id}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          group.group_status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : group.group_status === 'conflict'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {group.group_status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {group.member_count} members • {formatNumber(group.total_escrow)} coins in escrow
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openModal(group, 'transfer')}
                      className="btn btn-primary"
                    >
                      Transfer Escrow
                    </button>
                    <button
                      onClick={() => openModal(group, 'return')}
                      className="btn btn-secondary"
                    >
                      Return All
                    </button>
                  </div>
                </div>

                {/* Group Members */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Group Members</h4>
                  <div className="space-y-2">
                    {group.members?.map((member: any) => (
                      <div
                        key={member.user_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                          <p className="text-xs text-gray-400 mt-1">{member.title}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-primary-600">
                            {formatNumber(member.escrow_amount)} coins
                          </p>
                          <p className="text-xs text-gray-500">Escrow Held</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transfer Modal */}
      {showModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {modalAction === 'transfer' ? 'Transfer Escrow' : 'Return All Escrow'}
              </h2>
              <p className="text-gray-600 mt-1">
                Group #{selectedGroup.group_id} • {selectedGroup.member_count} members
              </p>
            </div>

            <div className="p-6 space-y-6">
              {modalAction === 'transfer' ? (
                <>
                  {/* Member Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-3">Group Members & Escrow</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedGroup.members?.map((member: any) => {
                        const finalBalance = member.escrow_amount - getTotalDeduction(member.user_id)

                        return (
                          <div key={member.user_id} className="bg-white p-3 rounded border">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {member.first_name} {member.last_name}
                                </p>
                                <p className="text-xs text-gray-500">{member.email}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-gray-700">
                                  {formatNumber(member.escrow_amount)} coins
                                </p>
                                <p className="text-xs text-gray-500">Available</p>
                              </div>
                            </div>

                            {transfers.length > 0 && (
                              <div className="mt-2 pt-2 border-t text-xs space-y-1">
                                {getTotalDeduction(member.user_id) > 0 && (
                                  <p className="text-red-600">
                                    Deducting: -{formatNumber(getTotalDeduction(member.user_id))}
                                  </p>
                                )}
                                {getTotalAddition(member.user_id) > 0 && (
                                  <p className="text-green-600">
                                    Receiving: +{formatNumber(getTotalAddition(member.user_id))}
                                  </p>
                                )}
                                {finalBalance > 0 && (
                                  <p className="text-blue-600">
                                    Returned: {formatNumber(finalBalance)}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Transfers */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900">Escrow Transfers</h3>
                      <button
                        onClick={addTransfer}
                        className="btn btn-sm btn-primary flex items-center space-x-1"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Transfer</span>
                      </button>
                    </div>

                    {transfers.length === 0 ? (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <p className="text-gray-500">
                          No transfers added yet. Click "Add Transfer" to specify how escrow should be allocated.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {transfers.map((transfer, index) => {
                          const fromMember = selectedGroup.members?.find(
                            (m: any) => m.user_id === transfer.from_user_id
                          )
                          const isOverdraft = getTotalDeduction(transfer.from_user_id) > (fromMember?.escrow_amount || 0)

                          return (
                            <div
                              key={index}
                              className={`border rounded-lg p-4 ${
                                isOverdraft ? 'border-red-300 bg-red-50' : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                  {/* From */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      From
                                    </label>
                                    <select
                                      value={transfer.from_user_id}
                                      onChange={(e) =>
                                        updateTransfer(index, 'from_user_id', Number(e.target.value))
                                      }
                                      className="select select-sm w-full"
                                    >
                                      {selectedGroup.members?.map((member: any) => (
                                        <option key={member.user_id} value={member.user_id}>
                                          {member.first_name} {member.last_name} ({formatNumber(member.escrow_amount)})
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {/* To */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      To
                                    </label>
                                    <select
                                      value={transfer.to_user_id}
                                      onChange={(e) =>
                                        updateTransfer(index, 'to_user_id', Number(e.target.value))
                                      }
                                      className="select select-sm w-full"
                                    >
                                      {selectedGroup.members?.map((member: any) => (
                                        <option key={member.user_id} value={member.user_id}>
                                          {member.first_name} {member.last_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {/* Amount */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Amount (coins)
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={transfer.amount || ''}
                                      onChange={(e) =>
                                        updateTransfer(index, 'amount', Number(e.target.value))
                                      }
                                      className="input input-sm w-full"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>

                                <button
                                  onClick={() => removeTransfer(index)}
                                  className="btn btn-sm btn-ghost text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>

                              {isOverdraft && (
                                <div className="mt-2 flex items-center text-red-600 text-xs">
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  <span>Insufficient escrow for this member</span>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // Return all mode
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-yellow-900">Return All Escrow</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        This will return all held escrow to the original owners. Total:{' '}
                        <span className="font-semibold">{formatNumber(selectedGroup.total_escrow)} coins</span>
                      </p>
                      <ul className="mt-3 space-y-1">
                        {selectedGroup.members?.map((member: any) => (
                          <li key={member.user_id} className="text-sm text-yellow-800">
                            • {member.first_name} {member.last_name}: {formatNumber(member.escrow_amount)} coins
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (for audit trail)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="textarea w-full"
                  rows={3}
                  placeholder="Describe the reason for this action..."
                />
              </div>

              {/* Close Group */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="close_group"
                  checked={closeGroup}
                  onChange={(e) => setCloseGroup(e.target.checked)}
                  className="checkbox"
                />
                <label htmlFor="close_group" className="ml-2 text-sm text-gray-700">
                  Close the barter group after completing this action
                </label>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={closeModal} className="btn btn-secondary">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={transferMutation.isPending || returnAllMutation.isPending}
                className="btn btn-primary"
              >
                {transferMutation.isPending || returnAllMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {modalAction === 'transfer' ? 'Execute Transfers' : 'Return All Escrow'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
